<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref, watch } from 'vue';

import LaisAuditEventList from 'dashboard/components-next/LaisAuditEventList.vue';
import LaisMetricStrip from 'dashboard/components-next/LaisMetricStrip.vue';
import LaisPayloadViewer from 'dashboard/components-next/LaisPayloadViewer.vue';
import LaisRunStatusPanel from 'dashboard/components-next/LaisRunStatusPanel.vue';
import LaisSourceHealthCard from 'dashboard/components-next/LaisSourceHealthCard.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

import PendingMarketOperationState from './PendingMarketOperationState.vue';

import {
  normalizePendingMarketOperationSnapshot,
  pendingMarketOperationPayloadForItem,
  pendingMarketOperationPropertyTitle,
  pendingMarketOperationReasonForItem,
  pendingMarketOperationStatusTone,
} from '../utils/pendingMarketOperations';

const props = defineProps({
  tipos: { type: Array, default: () => [] },
  activeKind: { type: String, default: '' },
  items: { type: Array, default: () => [] },
  pageMeta: { type: Object, default: () => ({}) },
  source: { type: String, default: '' },
  operationSnapshot: { type: Object, default: null },
  errorMessage: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  canViewOperations: { type: Boolean, default: true },
});

const emit = defineEmits(['retry']);

const selectedItemId = ref('');

const snapshot = computed(() =>
  normalizePendingMarketOperationSnapshot({
    kinds: props.tipos,
    items: props.items,
    activeKind: props.activeKind,
    pageMeta: props.pageMeta,
    source: props.source,
    operationSnapshot: props.operationSnapshot,
    errorMessage: props.errorMessage,
    loading: props.loading,
  })
);

watch(
  () => props.items,
  items => {
    if (!items.length) {
      selectedItemId.value = '';
      return;
    }

    if (!items.some(item => String(item.id) === String(selectedItemId.value))) {
      selectedItemId.value = String(items[0].id);
    }
  },
  { immediate: true }
);

const selectedItem = computed(
  () =>
    props.items.find(
      item => String(item.id) === String(selectedItemId.value)
    ) ||
    props.items[0] ||
    null
);

const selectedPayload = computed(() =>
  pendingMarketOperationPayloadForItem(selectedItem.value)
);

const selectedTitle = computed(() =>
  selectedItem.value
    ? pendingMarketOperationPropertyTitle(selectedItem.value)
    : 'Nenhum item selecionado'
);

const selectedReason = computed(() =>
  selectedItem.value
    ? pendingMarketOperationReasonForItem(selectedItem.value)
    : ''
);

const selectedTone = computed(() =>
  selectedItem.value
    ? pendingMarketOperationStatusTone(selectedItem.value.status)
    : 'neutral'
);

const operationQueues = computed(() => snapshot.value.queues || []);
const operationRuns = computed(() => snapshot.value.runs || []);
const importGroups = computed(() => snapshot.value.importGroups || []);
const notificationGroups = computed(
  () => snapshot.value.notificationGroups || []
);
const readiness = computed(() => snapshot.value.readiness || {});
const readinessChecks = computed(() => readiness.value.checks || []);
const readinessWarnings = computed(() => readiness.value.warnings || []);
const readinessBlockers = computed(() => readiness.value.blockers || []);
const firstReadinessReason = computed(() =>
  [...readinessWarnings.value, ...readinessBlockers.value]
    .map(reason => {
      if (typeof reason === 'string') return reason;
      return reason?.message || reason?.label || reason?.reason || '';
    })
    .find(Boolean)
);
const readinessTone = computed(() => {
  if (readiness.value.status === 'ready') return 'positive';
  if (readiness.value.status === 'blocked') return 'danger';
  if (readiness.value.status === 'warning') return 'warning';
  return 'neutral';
});
const readinessLabel = computed(() => {
  if (readiness.value.status === 'ready') return 'Pronto';
  if (readiness.value.status === 'blocked') return 'Bloqueado';
  if (readiness.value.status === 'warning') return 'Atenção';
  return 'Sem sinal';
});
const midasStatus = computed(() => snapshot.value.midas || {});
const midasTone = computed(() => {
  if (midasStatus.value.status === 'healthy') return 'positive';
  if (midasStatus.value.status === 'syncing') return 'info';
  if (midasStatus.value.status === 'stale') return 'warning';
  if (midasStatus.value.status === 'error') return 'danger';
  return 'neutral';
});
const midasLabel = computed(() => {
  if (midasStatus.value.status === 'healthy') return 'Saudável';
  if (midasStatus.value.status === 'syncing') return 'Sincronizando';
  if (midasStatus.value.status === 'stale') return 'Defasado';
  if (midasStatus.value.status === 'error') return 'Erro';
  return 'Sem sinal';
});

const formatNumber = value =>
  new Intl.NumberFormat('pt-BR').format(Number(value || 0));

const redactedPayloadKeys = [
  'authorization',
  'bank',
  'cnpj',
  'cpf',
  'document',
  'email',
  'name',
  'owner',
  'password',
  'phone',
  'pix',
  'secret',
  'token',
  'url',
];

const hasOperationError = computed(() => Boolean(props.errorMessage));
const hasNoOperationalData = computed(
  () =>
    !props.loading &&
    !hasOperationError.value &&
    props.items.length === 0 &&
    operationQueues.value.length === 0 &&
    operationRuns.value.length === 0 &&
    importGroups.value.length === 0 &&
    notificationGroups.value.length === 0 &&
    readinessChecks.value.length === 0 &&
    snapshot.value.timeline.length === 0 &&
    snapshot.value.sources.length === 0
);
const isStaleOperation = computed(
  () =>
    snapshot.value.runPanel?.stale ||
    snapshot.value.runPanel?.status === 'stale' ||
    midasStatus.value.status === 'stale'
);
const isPartialOperation = computed(
  () =>
    Boolean(props.operationSnapshot?.partial) ||
    readiness.value.status === 'warning' ||
    readinessWarnings.value.length > 0 ||
    readinessBlockers.value.length > 0
);
const operationStateBanners = computed(() =>
  [
    isPartialOperation.value
      ? {
          key: 'partial',
          icon: 'i-lucide-circle-alert',
          title: 'Snapshot parcial',
          description:
            firstReadinessReason.value ||
            'Parte das fontes operacionais nao respondeu. Use leitura e auditoria, mas nao trate o cockpit como completo.',
        }
      : null,
    isStaleOperation.value
      ? {
          key: 'stale',
          icon: 'i-lucide-activity',
          title: 'Sinal defasado',
          description:
            'O ultimo processamento esta stale. Recarregue e confira workers antes de acionar envios ou conciliacoes.',
        }
      : null,
  ].filter(Boolean)
);
</script>

<template>
  <PendingMarketOperationState
    :can-view-operations="canViewOperations"
    :has-operation-error="hasOperationError"
    :error-message="errorMessage"
    :loading="loading"
    :has-no-operational-data="hasNoOperationalData"
    :banners="operationStateBanners"
    @retry="emit('retry')"
  >
    <LaisMetricStrip
      :items="snapshot.metrics"
      :loading="loading"
      aria-label="Resumo operacional de pendentes"
    />

    <div class="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(22rem,0.9fr)]">
      <LaisRunStatusPanel v-bind="snapshot.runPanel" />

      <section
        class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 text-lais-text shadow-lais-card"
        aria-labelledby="pending-market-locks-title"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <h3
              id="pending-market-locks-title"
              class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
            >
              Locks e filas esperadas
            </h3>
          </div>
          <button
            type="button"
            class="inline-grid size-9 shrink-0 place-items-center rounded-lais border border-lais-border-soft bg-lais-bg-soft text-lais-text-muted transition hover:border-lais-primary hover:text-lais-primary"
            aria-label="Recarregar operação"
            @click="emit('retry')"
          >
            <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
          </button>
        </div>

        <ul class="mt-4 grid gap-3" aria-label="Locks monitorados">
          <li
            v-for="lock in snapshot.locks"
            :key="lock.id"
            class="grid gap-1 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-2"
          >
            <div class="flex min-w-0 items-center justify-between gap-2">
              <span class="truncate text-lais-body-sm font-semibold">
                {{ lock.label }}
              </span>
              <LaisStatusBadge
                :tone="lock.tone"
                :label="lock.status"
                size="sm"
                dot
              />
            </div>
            <code class="break-all text-lais-body-xs text-lais-text-helper">
              {{ lock.key }}
            </code>
            <p class="text-lais-body-xs text-lais-text-muted">
              {{ lock.helper }}
            </p>
          </li>
        </ul>
      </section>
    </div>

    <div class="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <section
        class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 text-lais-text shadow-lais-card"
        aria-labelledby="pending-market-import-groups-title"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3
              id="pending-market-import-groups-title"
              class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
            >
              Importar Anúncios
            </h3>
          </div>
          <LaisStatusBadge
            :label="importGroups.length ? 'Configurado' : 'Sem grupo'"
            :tone="importGroups.length ? 'positive' : 'warning'"
            icon="i-lucide-message-square-text"
          />
        </div>

        <div class="mt-4 grid gap-3">
          <article
            v-for="group in importGroups"
            :key="group.id"
            class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-2"
          >
            <div class="flex min-w-0 items-center justify-between gap-2">
              <span class="truncate text-lais-body-sm font-semibold">
                {{ group.label }}
              </span>
              <LaisStatusBadge
                :tone="group.tone"
                :label="group.status"
                size="sm"
                dot
              />
            </div>
            <dl class="mt-2 grid grid-cols-3 gap-2 text-lais-body-xs">
              <div>
                <dt class="text-lais-text-helper">Reconhecidas</dt>
                <dd class="font-semibold text-lais-text">
                  {{ formatNumber(group.recognized) }}
                </dd>
              </div>
              <div>
                <dt class="text-lais-text-helper">Enfileiradas</dt>
                <dd class="font-semibold text-lais-text">
                  {{ formatNumber(group.enqueued) }}
                </dd>
              </div>
              <div>
                <dt class="text-lais-text-helper">Falhas</dt>
                <dd class="font-semibold text-lais-text">
                  {{ formatNumber(group.failed) }}
                </dd>
              </div>
            </dl>
            <div
              v-if="group.parser"
              class="mt-3 rounded-lais-sm border border-lais-border-soft bg-lais-bg-card px-3 py-2"
            >
              <div class="flex min-w-0 items-center justify-between gap-2">
                <span class="text-lais-body-xs font-semibold text-lais-text">
                  Parser
                </span>
                <LaisStatusBadge
                  :label="group.parser.status || 'sem sinal'"
                  :tone="group.parser.status === 'failed' ? 'danger' : 'info'"
                  size="sm"
                  dot
                />
              </div>
              <dl class="mt-2 grid grid-cols-2 gap-2 text-lais-body-xs">
                <div>
                  <dt class="text-lais-text-helper">Linhas</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ formatNumber(group.parser.parsedRows) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-lais-text-helper">Atualizadas</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ formatNumber(group.parser.upsertedRows) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-lais-text-helper">Duplicadas</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ formatNumber(group.parser.duplicateRows) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-lais-text-helper">Pend. verificação</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ formatNumber(group.parser.verificationPending) }}
                  </dd>
                </div>
              </dl>
              <p
                v-if="group.parser.latestReason"
                class="mt-2 text-lais-body-xs text-lais-text-helper"
              >
                {{ group.parser.latestReason }}
              </p>
            </div>
            <code
              v-if="group.fingerprint"
              class="mt-2 block break-all text-lais-body-xs text-lais-text-helper"
            >
              fp: {{ group.fingerprint }}
            </code>
          </article>

          <p
            v-if="!importGroups.length"
            class="rounded-lais-sm border border-dashed border-lais-border-soft bg-lais-bg-soft px-3 py-4 text-lais-body-sm text-lais-text-muted"
          >
            Nenhum grupo de importação autorizado no snapshot operacional.
          </p>
        </div>

        <div class="mt-4 border-t border-lais-border-soft pt-3">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <span class="text-lais-body-sm font-semibold text-lais-text">
              Grupos de envio
            </span>
            <LaisStatusBadge
              :label="formatNumber(notificationGroups.length)"
              tone="info"
              size="sm"
            />
          </div>
          <div
            v-if="notificationGroups.length"
            class="mt-2 flex flex-wrap gap-2"
          >
            <span
              v-for="group in notificationGroups"
              :key="group.id"
              class="inline-flex items-center gap-1 rounded-full border border-lais-border-soft bg-lais-bg-soft px-2 py-1 text-lais-body-xs text-lais-text-helper"
            >
              <span class="i-lucide-users-round size-3" aria-hidden="true" />
              {{ group.label }}
            </span>
          </div>
        </div>
      </section>

      <section
        class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 text-lais-text shadow-lais-card"
        aria-labelledby="pending-market-midas-readiness-title"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3
              id="pending-market-midas-readiness-title"
              class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
            >
              Midas e readiness
            </h3>
          </div>
          <div class="flex flex-wrap gap-2">
            <LaisStatusBadge :label="midasLabel" :tone="midasTone" dot />
            <LaisStatusBadge
              :label="readinessLabel"
              :tone="readinessTone"
              icon="i-lucide-shield-check"
            />
          </div>
        </div>

        <dl class="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          <div class="rounded-lais-sm bg-lais-bg-soft px-3 py-2">
            <dt class="text-lais-body-xs text-lais-text-helper">Opcionistas</dt>
            <dd class="text-lais-body-sm font-semibold text-lais-text">
              {{ formatNumber(midasStatus.optionistCount) }}
            </dd>
          </div>
          <div class="rounded-lais-sm bg-lais-bg-soft px-3 py-2">
            <dt class="text-lais-body-xs text-lais-text-helper">
              Ações pendentes
            </dt>
            <dd class="text-lais-body-sm font-semibold text-lais-text">
              {{ formatNumber(midasStatus.pendingActions) }}
            </dd>
          </div>
          <div class="rounded-lais-sm bg-lais-bg-soft px-3 py-2">
            <dt class="text-lais-body-xs text-lais-text-helper">
              Ações com erro
            </dt>
            <dd class="text-lais-body-sm font-semibold text-lais-text">
              {{ formatNumber(midasStatus.failedActions) }}
            </dd>
          </div>
          <div class="rounded-lais-sm bg-lais-bg-soft px-3 py-2">
            <dt class="text-lais-body-xs text-lais-text-helper">
              Último espelho
            </dt>
            <dd class="truncate text-lais-body-sm font-semibold text-lais-text">
              {{ midasStatus.lastActivityAt || '—' }}
            </dd>
          </div>
        </dl>

        <ul class="mt-4 grid gap-2" aria-label="Checks de readiness">
          <li
            v-for="check in readinessChecks"
            :key="check.id"
            class="flex min-w-0 items-center justify-between gap-2 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-2"
          >
            <span class="truncate text-lais-body-sm text-lais-text">
              {{ check.label }}
            </span>
            <LaisStatusBadge
              :label="check.status === 'ok' ? 'OK' : 'Pendente'"
              :tone="check.status === 'ok' ? 'positive' : 'warning'"
              size="sm"
              dot
            />
          </li>
        </ul>
      </section>
    </div>

    <section
      class="grid gap-3"
      aria-labelledby="pending-market-source-health-title"
    >
      <div class="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3
            id="pending-market-source-health-title"
            class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
          >
            Fontes observadas no filtro
          </h3>
        </div>
        <LaisStatusBadge
          :label="
            source === 'database' ? 'Dados ao vivo' : source || 'Fonte externa'
          "
          tone="info"
          icon="i-lucide-database"
        />
      </div>

      <div
        v-if="snapshot.sources.length"
        class="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
      >
        <LaisSourceHealthCard
          v-for="provider in snapshot.sources"
          :key="provider.name"
          v-bind="provider"
        />
      </div>
      <p
        v-else
        class="rounded-lais border border-dashed border-lais-border-soft bg-lais-bg-card px-4 py-5 text-lais-body-sm text-lais-text-muted"
      >
        Nenhuma fonte foi observada com os filtros atuais.
      </p>
    </section>

    <section
      v-if="operationQueues.length || operationRuns.length"
      class="grid gap-3"
      aria-labelledby="pending-market-queues-title"
    >
      <h3
        id="pending-market-queues-title"
        class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
      >
        Filas e últimos runs
      </h3>
      <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <article
          v-for="queue in operationQueues"
          :key="queue.id"
          class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-3 shadow-lais-card"
        >
          <div class="flex min-w-0 items-center justify-between gap-2">
            <span
              class="truncate text-lais-body-sm font-semibold text-lais-text"
            >
              {{ queue.name }}
            </span>
            <LaisStatusBadge
              :label="formatNumber(queue.size)"
              :tone="queue.size ? 'info' : 'neutral'"
              size="sm"
            />
          </div>
        </article>
      </div>
    </section>

    <div
      class="grid gap-4 xl:grid-cols-[minmax(18rem,0.82fr)_minmax(0,1.18fr)]"
    >
      <section
        class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card"
        aria-labelledby="pending-market-timeline-title"
      >
        <h3
          id="pending-market-timeline-title"
          class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
        >
          Linha de auditoria
        </h3>
        <div class="mt-4">
          <LaisAuditEventList
            :events="snapshot.timeline"
            empty-label="Nenhum evento operacional no filtro atual."
            aria-label="Linha de auditoria de pendentes"
          />
        </div>
      </section>

      <section
        class="grid gap-3"
        aria-labelledby="pending-market-payload-title"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <h3
              id="pending-market-payload-title"
              class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
            >
              Payload redigido do item
            </h3>
          </div>
          <LaisStatusBadge
            v-if="selectedItem"
            :tone="selectedTone"
            :label="selectedItem.statusLabel || selectedItem.status"
            dot
          />
        </div>

        <div
          v-if="items.length"
          class="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          aria-label="Itens com payload disponivel"
        >
          <button
            v-for="item in items.slice(0, 8)"
            :key="item.id"
            type="button"
            data-testid="pending-operation-item"
            class="inline-flex min-h-10 min-w-[12rem] shrink-0 items-center gap-2 rounded-lais border px-3 text-left text-lais-body-sm font-semibold transition focus-visible:shadow-lais-focus"
            :class="
              String(selectedItemId) === String(item.id)
                ? 'border-lais-primary bg-lais-primary text-white'
                : 'border-lais-border-soft bg-lais-bg-card text-lais-text hover:border-lais-primary hover:text-lais-primary'
            "
            @click="selectedItemId = String(item.id)"
          >
            <span
              class="i-lucide-file-search size-4 shrink-0"
              aria-hidden="true"
            />
            <span class="min-w-0 truncate">
              #{{ item.id }} - {{ item.statusLabel || item.status }}
            </span>
          </button>
        </div>

        <div
          v-if="selectedItem"
          class="rounded-lais border border-lais-border-soft bg-lais-bg-soft px-4 py-3"
        >
          <p class="truncate text-lais-body-sm font-semibold text-lais-text">
            {{ selectedTitle }}
          </p>
          <p class="mt-1 text-lais-body-sm text-lais-text-helper">
            {{ selectedReason }}
          </p>
        </div>

        <LaisPayloadViewer
          title="Payload operacional redigido"
          schema-version="pending-market/v1"
          :payload="selectedPayload"
          :redacted-keys="redactedPayloadKeys"
          :collapsed="false"
        />
      </section>
    </div>
  </PendingMarketOperationState>
</template>
